import {useHistory} from "react-router-dom";
import {useSelector} from "react-redux";
import {BetbyBanner} from "../../contenful/types";
import {getContainer} from "../../ioc/IOCSetup";
import {AppState} from "../../RootReducer";
import {useEffect, useMemo, useRef, useState} from "react";
import {BetbyParamters, handleConfig, InitializeBetbyConfig} from "../../sports/providers/BetbyClient";
import {LaunchBetbyResponse, Settings, SportsbookProvider} from "../../http/protocol";
import {Locale} from "../../contenful/types";
import './styles/BetbyBannerSectionComponent.scss';

interface Props {
    section: BetbyBanner
}

/**
 * Get the path name for the sportsbook.
 * // example: multiple sportsbook path: /sportsbook/betby
 * // example: single sportsbook path: /sportsbook
 * @param settings
 */
const getPathName = (settings: Settings | undefined) => {
    const multipleSportsbookConfig = settings?.sportsbookConfig;

    const betbyConfig = multipleSportsbookConfig?.enabledConfig
        ? multipleSportsbookConfig?.providers?.find((provider) => provider.provider === SportsbookProvider.BETBY)
        : undefined;

    const configurablePath = betbyConfig?.pathname || '';
    const path = configurablePath ? `sportsbook/${configurablePath}` : `sportsbook`;

    return path;
}

const getIsBetbyEnabled = (settings: Settings | undefined) => {
    const isSportsbookEnabled = settings?.sportsbookEnabled;

    // check if betby is main sportsbook provider
    const isBetbySportsbookEnabled = settings?.sportsbookProvider === SportsbookProvider.BETBY;

    // check if betby is one of the multiple sportsbook providers
    const isBetbyInMultipleSportsbook = settings?.sportsbookConfig?.providers?.find((provider) => provider.provider === SportsbookProvider.BETBY);

    return (isSportsbookEnabled && (isBetbySportsbookEnabled || isBetbyInMultipleSportsbook));
}

export const BetByBannerWrapper = (props: Props) => {
    const userLoaded = useSelector((app: AppState) => app.user.userInfoLoaded) || false;
    const settings = useSelector((app: AppState) => app.settings.settings);
    const isBetbyEnabled = getIsBetbyEnabled(settings);

    const isLoggedIn = useSelector((app: AppState) => app.user.user?.loggedIn, (prev, curr) => prev === curr) || false;
    const activeCurrency = useSelector((app: AppState) => app.user.activeCurrency);
    const currency = useMemo(() => activeCurrency || 'EUR', [activeCurrency]);

    const locale = getContainer().getSettingsService().getActiveLocale();
    const path = getPathName(settings);

    // Use key to force remount when isLoggedIn changes
    const key = useMemo(() => `${isLoggedIn}`, [isLoggedIn]);

    // Ensure the component doesn't render or mount until userLoaded is true
    if (!userLoaded || !isBetbyEnabled) {
        return null;
    }

    return (
        <div className={'betbyBannerSectionComponent'}>
            <div className={'container'}>
                <div className="section-outer">
                    <div className="inner-section desktop">
                        <BetByBanner
                            key={key} // Component will unmount and remount when `isLoggedIn` changes
                            section={props.section}
                            path={path}
                            locale={locale}
                            loggedIn={isLoggedIn}
                            currency={currency}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

interface BetByBannerProps {
    section: BetbyBanner
    path: string
    loggedIn: boolean
    currency: string
    locale: Locale
}

const generateElementId = (length: number): string => {
    const randomString = Math.random().toString(36).substring(2, 2 + length);
    return `strapi_banner_${randomString}`;
}

const BetByBanner = (props: BetByBannerProps) => {
    const history = useHistory();
    const [config, setConfig] = useState<BetbyParamters>();
    const btRendererRef = useRef<BTRenderer | undefined>(undefined);
    const bannerRef = useRef<HTMLDivElement | null>(null);

    // this must be unique for each instance of the component
    const htmlIdTag = generateElementId(10);

    // Fetch and set betby config
    useEffect(() => {
        const fetchAndSetBetbyConfig = async () => {
            try {
                let fetchedConfig: LaunchBetbyResponse | undefined = undefined;
                if (props.loggedIn) {
                    fetchedConfig = await getContainer().getSportsbookService().getBetbyGameLaunchUrl(props.locale.code, props.currency);
                } else {
                    fetchedConfig = await getContainer().getSportsbookService().getBetbyAnonymousGameLaunchUrl();
                }
                setConfig(handleConfig(fetchedConfig, props.locale.code));
            } catch (e) {
                console.error(e);
            }
        };

        fetchAndSetBetbyConfig();
    }, []);

    // Render Betby banner
    useEffect(() => {
        if (!config?.config) {
            return;
        }

        // in betby backoffice, a banner config can apply to multiple placeholders like 'operator_page1', 'operator_page2', etc.
        // in cms, a betby banner component can only have one placeholder
        const placeholder = props.section.place_holder;
        const path = props.path

        const renderBetByBanner = (config: InitializeBetbyConfig) => {
            try {
                btRendererRef.current = new BTRenderer().initialize({
                    brand_id: config.brand_id,
                    token: config.token,
                    lang: config.lang,
                    target: bannerRef.current,
                    themeName: config.themeName,
                    widgetName: "promo",
                    widgetParams: {
                        placeholder: placeholder,
                        onBannerClick: (args: { url: string }) => {
                            history.push({
                                pathname: path,
                                search: new URLSearchParams({"bt-path": args.url}).toString(),
                            });
                        },
                    },
                });
            } catch (e) {
                console.error("error rendering betby banner", e);
            }
        };
        renderBetByBanner(config.config);
        return () => {
            // Clean up btRenderer on unmount
            btRendererRef.current?.kill();
            btRendererRef.current = undefined;
        };
    }, [config]);

    return (
        <>
            <div ref={bannerRef} id={htmlIdTag}/>
        </>
    )

}